import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { HERO_SLIDES } from "../data/mock";

const HeroSlider = () => {
  const [idx, setIdx] = useState(0);
  const slide = HERO_SLIDES[idx];

  useEffect(() => {
    const duration = slide.duration || 7000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), duration);
    return () => clearTimeout(t);
  }, [idx, slide]);

  const prev = () => setIdx((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const next = () => setIdx((i) => (i + 1) % HERO_SLIDES.length);

  return (
    <div className={`relative overflow-hidden rounded-lg bg-gradient-to-br ${slide.accent} h-[440px] lg:h-[480px] transition-all duration-700`}>
      {/* decorative bubbles */}
      <div className="absolute inset-0 pointer-events-none">
        <span className="absolute top-8 left-10 w-3 h-3 rounded-full bg-white/70" />
        <span className="absolute top-24 right-32 w-2 h-2 rounded-full bg-white/60" />
        <span className="absolute top-1/2 left-1/3 w-4 h-4 rounded-full bg-white/50" />
        <span className="absolute bottom-12 left-16 w-2.5 h-2.5 rounded-full bg-white/60" />
        <span className="absolute bottom-32 right-1/4 w-3 h-3 rounded-full bg-white/70" />
        <span className="absolute top-1/3 right-12 w-2 h-2 rounded-full bg-white/50" />
        <span className="absolute top-20 left-1/2 w-1.5 h-1.5 rounded-full bg-white/60" />
      </div>

      {slide.type === "rebrand" ? (
        <RebrandSlide slide={slide} key={`rebrand-${idx}`} />
      ) : (
        <div className="relative h-full max-w-3xl px-10 lg:px-16 flex flex-col justify-center text-white">
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase opacity-90 mb-4">
            <span className="w-8 h-px bg-white/70" /> {slide.badge}
          </span>
          <h1 className="text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-5 drop-shadow-sm">
            {slide.title}
          </h1>
          <p className="text-base lg:text-lg opacity-95 mb-8 max-w-xl leading-relaxed">{slide.subtitle}</p>
          <Link
            to={slide.href}
            className="inline-flex items-center gap-2 bg-white text-zinc-900 px-7 py-3.5 rounded-md text-sm font-semibold w-fit hover:bg-zinc-900 hover:text-white transition-colors"
          >
            {slide.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      {/* save badge (hidden on rebrand slide) */}
      {slide.type !== "rebrand" && (
        <div className="absolute right-10 lg:right-20 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center justify-center w-32 h-32 rounded-full bg-teal-400 text-white shadow-lg shadow-teal-900/20 rotate-[-8deg]">
          <span className="text-xs uppercase tracking-widest font-medium">Save</span>
          <span className="text-3xl font-bold leading-none mt-1">$8.00</span>
        </div>
      )}

      {/* nav */}
      <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full text-white/80 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors z-10">
        <ChevronLeft className="w-7 h-7" />
      </button>
      <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full text-white/80 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors z-10">
        <ChevronRight className="w-7 h-7" />
      </button>

      {/* dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-white w-8" : "bg-white/50 hover:bg-white/80"}`} />
        ))}
      </div>
    </div>
  );
};

/* ---------- Rebrand slide with animated name swap ---------- */
const RebrandSlide = ({ slide }) => {
  return (
    <div className="relative h-full px-6 sm:px-10 lg:px-16 flex flex-col items-center justify-center text-white text-center">
      {/* Sparkles + confetti dots */}
      <span className="absolute top-12 left-1/4 text-amber-200 animate-pulse"><Sparkles className="w-5 h-5" /></span>
      <span className="absolute top-20 right-1/4 text-white/70 animate-pulse" style={{ animationDelay: "0.7s" }}><Sparkles className="w-4 h-4" /></span>
      <span className="absolute bottom-20 left-1/3 text-amber-200/80 animate-pulse" style={{ animationDelay: "1.3s" }}><Sparkles className="w-3 h-3" /></span>

      {/* Badge */}
      <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase bg-white/15 backdrop-blur px-4 py-2 rounded-full mb-6 border border-white/20">
        <Sparkles className="w-3 h-3" /> {slide.badge}
      </span>

      {/* "is now" intro */}
      <div className="text-base sm:text-lg font-medium tracking-wide text-white/80 mb-2">
        We're proud to announce
      </div>

      {/* Animated name swap */}
      <div className="relative w-full max-w-4xl h-24 sm:h-32 lg:h-40 mb-3">
        <span className="rebrand-old absolute inset-0 flex items-center justify-center text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight whitespace-nowrap" style={{ textShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
          Design Plus
        </span>
        <span className="rebrand-new absolute inset-0 flex items-center justify-center text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight whitespace-nowrap bg-gradient-to-r from-amber-200 via-white to-amber-100 bg-clip-text text-transparent" style={{ filter: "drop-shadow(0 4px 24px rgba(255,255,255,0.4))" }}>
          Let's Print
        </span>
      </div>

      {/* Connector */}
      <div className="flex items-center gap-3 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-white/90 mb-5">
        <span className="w-10 sm:w-16 h-px bg-white/40" />
        <span className="rebrand-isnow">is now</span>
        <span className="w-10 sm:w-16 h-px bg-white/40" />
      </div>

      {/* Subtitle */}
      <p className="text-sm sm:text-base lg:text-lg text-white/90 max-w-2xl leading-relaxed mb-7">
        {slide.subtitle}
      </p>

      {/* CTA + assurance */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <a href="https://www.letsprint.ai" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-rose-600 px-7 py-3.5 rounded-md text-sm font-bold hover:bg-zinc-900 hover:text-white transition-colors">
          {slide.cta} <ArrowRight className="w-4 h-4" />
        </a>
        <span className="text-xs text-white/75">Same Commitment · Same team · Bigger Vision</span>
      </div>
    </div>
  );
};

export default HeroSlider;
